/**
 * FASE L1 — Admin Cockpit (home operatore unificata).
 *
 * Stream card semplificato + filtro Mie/Tutte + azioni inline + accordion riuso.
 *
 * Endpoint: GET /api/admin/cockpit/stream
 * Riusa: RichiestaRiepilogoAccordion (iter71) per drill-down inline.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, RefreshCw, ArrowLeft, ChevronDown, ChevronRight, Phone,
  MessageCircle, Mail, ClipboardCheck, Flame, Filter, X, Check,
} from "lucide-react";
import RichiestaRiepilogoAccordion from "@/components/admin/RichiestaRiepilogoAccordion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = [
  { key: "", label: "Tutti gli stati" },
  { key: "nuovo", label: "Nuovo" },
  { key: "in_lavorazione", label: "In lavorazione" },
  { key: "preventivato", label: "Preventivato" },
  { key: "chiuso_positivo", label: "Chiuso positivo" },
  { key: "chiuso_negativo", label: "Chiuso negativo" },
];

export default function AdminCockpitPage({ embedded = false }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [config, setConfig] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [countsByMacro, setCountsByMacro] = useState({});
  const [countsBySub, setCountsBySub] = useState({});
  const [countsByCanale, setCountsByCanale] = useState({ convenzione: 0, diretto: 0 });
  const [canale, setCanale] = useState("tutte");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [scope, setScope] = useState(null);  // Impostato dopo config load basato sul ruolo
  // FIX 2 (feb-2026) — Filtro Ramo gerarchico multi-select:
  //   • macro: Set<string> macro-rami selezionati interamente (tutti i sub)
  //   • subs:  Set<string> sub-rami selezionati esplicitamente (formato "macro:sub")
  // Se sia macro che subs vuoti → nessun filtro (tutte le richieste).
  const [macroSet, setMacroSet] = useState(() => new Set());
  const [subSet, setSubSet] = useState(() => new Set());
  const [status, setStatus] = useState("");
  const [partner, setPartner] = useState("");
  const [ramoDropdownOpen, setRamoDropdownOpen] = useState(false);
  const [ramoExpanded, setRamoExpanded] = useState(() => new Set());  // macro espansi nel dropdown

  useEffect(() => {
    axios.get(`${API}/admin/cockpit/config`, { withCredentials: true })
      .then(({ data }) => {
        setConfig(data);
        // HOTFIX 27-lug: default scope basato sul ruolo backend.
        // super_admin/admin → "all" (vedono tutto per default)
        // dipendente → "me" (RBAC-forced backend)
        const role = (data?.user?.role || "").toLowerCase();
        const defaultScope = (role === "dipendente") ? "me" : "all";
        setScope(prev => prev ?? defaultScope);
      })
      .catch(() => {
        toast.error("Errore caricamento config");
        setScope("all");  // fallback sicuro
      });
  }, []);

  const load = useCallback(async (p = 1) => {
    if (!scope) return;  // HOTFIX — non fetchare finché config non ha determinato lo scope default
    setLoading(true);
    try {
      const params = { scope, page: p, page_size: 25 };
      // FIX 2 — invio CSV di macro e sub selezionati (multi-select gerarchico)
      if (macroSet.size > 0) params.macro = Array.from(macroSet).join(",");
      if (subSet.size > 0) {
        // subSet contiene "macro:sub" — estraggo solo il sub per il param
        params.sub = Array.from(subSet).map(k => k.split(":")[1]).join(",");
      }
      if (status) params.status = status;
      if (partner) params.partner = partner;
      if (canale && canale !== "tutte") params.canale = canale;
      const { data } = await axios.get(`${API}/admin/cockpit/stream`,
        { params, withCredentials: true });
      setItems(data.items); setTotal(data.total); setPage(data.page);
      setCountsByMacro(data.counts_by_macro || {});
      setCountsBySub(data.counts_by_sub || {});
      setCountsByCanale(data.counts_by_canale || { convenzione: 0, diretto: 0 });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore stream");
    } finally { setLoading(false); }
  }, [scope, macroSet, subSet, status, partner, canale]);

  useEffect(() => { load(1); }, [load]);

  // FASE L2.1 — highlight ID da Cmd+K deep-link
  useEffect(() => {
    const hid = searchParams.get("highlight");
    if (hid && items.length > 0 && items.some(it => it.id === hid)) {
      setExpandedId(hid);
      setTimeout(() => {
        const el = document.querySelector(`[data-highlight-id="${hid}"]`);
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
      // Rimuovi il param dopo il primo scroll per evitare loop
      const params = Object.fromEntries(searchParams.entries());
      delete params.highlight;
      setSearchParams(params, { replace: true });
    }
  }, [items, searchParams, setSearchParams]);

  const totalPages = Math.max(1, Math.ceil(total / 25));

  return (
    <div className={embedded ? "bg-slate-50" : "min-h-screen bg-slate-50"} data-testid="admin-cockpit-page">
      {/* Header sticky — nascosto in modalità embedded */}
      {!embedded && (
        <div className="bg-white border-b border-slate-200 sticky top-0 z-30">
          <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate("/admin/dashboard")}
                      className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-[#163321]"
                      data-testid="cockpit-back-btn">
                <ArrowLeft className="w-4 h-4" /> Dashboard
              </button>
              <div className="h-4 w-px bg-slate-300" />
              <h1 className="font-heading text-lg font-semibold text-[#0F1F14]"
                  data-testid="cockpit-title">
                🎯 Cockpit Operatore
              </h1>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold uppercase">
                nuovo
              </span>
            </div>
            {/* v1.58 — Segmented control scope Mie/Tutte (prominente in header) */}
            <div className="flex items-center gap-2 flex-1 justify-center md:justify-end min-w-0"
                  data-testid="cockpit-scope-toggle-header">
              <div className="inline-flex items-stretch rounded-full border-2 border-[#163321]/20 bg-slate-100 p-0.5 shadow-sm">
                {[{k:"me",l:"Le mie richieste"},{k:"all",l:"Tutte"}].map((sc) => (
                  <button key={sc.k}
                          onClick={() => setScope(sc.k)}
                          className={`text-xs sm:text-sm px-3 sm:px-4 py-1.5 rounded-full font-semibold transition-all
                            ${scope === sc.k
                              ? "bg-[#163321] text-white shadow"
                              : "text-slate-700 hover:text-[#163321]"}`}
                          data-testid={`cockpit-scope-header-${sc.k}`}>
                    {sc.l}
                  </button>
                ))}
              </div>
              <span className="text-xs text-slate-500 hidden sm:inline">
                <span className="font-heading text-lg text-[#163321]" data-testid="cockpit-total-count">{total}</span> richieste
              </span>
              <button onClick={() => load(page)} disabled={loading}
                      className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-50"
                      data-testid="cockpit-refresh-btn">
                {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                Aggiorna
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={embedded ? "" : "max-w-6xl mx-auto px-4 py-5"}>
        {/* Mini toolbar solo per modalità embedded (contatore + refresh) */}
        {embedded && (
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs text-slate-500">
              <span className="font-heading text-lg text-[#163321]" data-testid="cockpit-total-count">{total}</span> richieste totali
            </span>
            <button onClick={() => load(page)} disabled={loading}
                    className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-50 bg-white"
                    data-testid="cockpit-refresh-btn">
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              Aggiorna
            </button>
          </div>
        )}
        {/* HOTFIX 27-lug — KPI bar cross-ramo (contatori per macro) */}
        {Object.keys(countsByMacro).length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3" data-testid="cockpit-kpi-bar">
            {(config?.macro_rami || []).map((m) => {
              const count = countsByMacro[m.id] || 0;
              if (count === 0) return null;
              const active = macroSet.has(m.id);
              return (
                <button key={m.id}
                        onClick={() => {
                          // FIX 2 — Toggle macro nel Set (mutuamente esclusivo con sub del medesimo macro)
                          setMacroSet(prev => {
                            const next = new Set(prev);
                            if (next.has(m.id)) next.delete(m.id); else next.add(m.id);
                            return next;
                          });
                          // Se attivo un macro intero, rimuovo eventuali sub selezionati per quel macro
                          setSubSet(prev => {
                            const next = new Set(prev);
                            for (const k of Array.from(next)) {
                              if (k.startsWith(`${m.id}:`)) next.delete(k);
                            }
                            return next;
                          });
                        }}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs transition-all
                          ${active
                            ? "bg-[#163321] text-white border-[#163321]"
                            : "bg-white text-slate-700 border-slate-200 hover:border-[#163321]/40"}`}
                        data-testid={`cockpit-kpi-${m.id}`}>
                  <span className="text-sm">{m.icon}</span>
                  <span className="font-medium">{m.label}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono
                    ${active ? "bg-white/20" : "bg-slate-100 text-slate-600"}`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* HOTFIX 27-lug — Toggle Canale (convenzione/diretto/tutte) */}
        {(countsByCanale.convenzione > 0 || countsByCanale.diretto > 0) && (
          <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="cockpit-canale-toggle">
            <span className="text-[11px] text-slate-500 uppercase font-semibold">Canale:</span>
            {[
              { k: "tutte", l: "Tutte", n: (countsByCanale.convenzione + countsByCanale.diretto), icon: "🌐" },
              { k: "convenzione", l: "In convenzione", n: countsByCanale.convenzione, icon: "🎫" },
              { k: "diretto", l: "Fuori convenzione", n: countsByCanale.diretto, icon: "🏠" },
            ].map(({k, l, n, icon}) => (
              <button key={k}
                      onClick={() => setCanale(k)}
                      className={`flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs transition-all
                        ${canale === k
                          ? "bg-[#163321] text-white border-[#163321]"
                          : "bg-white text-slate-700 border-slate-200 hover:border-[#163321]/40"}`}
                      data-testid={`cockpit-canale-${k}`}>
                <span>{icon}</span>
                <span className="font-medium">{l}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono
                  ${canale === k ? "bg-white/20" : "bg-slate-100 text-slate-600"}`}>
                  {n}
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Filtri */}
        <div className="bg-white border border-slate-200 rounded-2xl p-4 mb-4 shadow-sm"
             data-testid="cockpit-filters">
          {/* Scope toggle inline (secondario) — v1.58: rimosso, sostituito da segmented control in header */}
          <div className="flex flex-wrap items-center gap-2 mb-3">
            {/* FIX 1 (feb-2026) — Rimossi toggle Stream/Kanban/Tabella. Solo Stream view attiva.
                Codice KanbanView/TableView mantenuto in coda al file per riattivazione futura. */}
          </div>
          {/* Filtri secondari */}
          <div className="flex flex-wrap items-center gap-2 text-xs">
            {/* FIX 2 (feb-2026) — Filtro Ramo GERARCHICO multi-select con accordion per sub-tipi */}
            <RamoHierarchicalFilter
              config={config}
              macroSet={macroSet}
              subSet={subSet}
              onMacroToggle={(macroId) => {
                setMacroSet(prev => {
                  const next = new Set(prev);
                  if (next.has(macroId)) next.delete(macroId); else next.add(macroId);
                  return next;
                });
                setSubSet(prev => {
                  const next = new Set(prev);
                  for (const k of Array.from(next)) {
                    if (k.startsWith(`${macroId}:`)) next.delete(k);
                  }
                  return next;
                });
              }}
              onSubToggle={(macroId, subId) => {
                const key = `${macroId}:${subId}`;
                setSubSet(prev => {
                  const next = new Set(prev);
                  if (next.has(key)) next.delete(key); else next.add(key);
                  return next;
                });
                // Se seleziono un sub, rimuovo l'attivazione del macro intero (esclusivo)
                setMacroSet(prev => {
                  const next = new Set(prev);
                  next.delete(macroId);
                  return next;
                });
              }}
              onClear={() => { setMacroSet(new Set()); setSubSet(new Set()); }}
              expanded={ramoExpanded}
              onExpandToggle={(macroId) => {
                setRamoExpanded(prev => {
                  const next = new Set(prev);
                  if (next.has(macroId)) next.delete(macroId); else next.add(macroId);
                  return next;
                });
              }}
              open={ramoDropdownOpen}
              onOpenChange={setRamoDropdownOpen}
              countsByMacro={countsByMacro}
              countsBySub={countsBySub}
            />
            <select value={status} onChange={(e) => setStatus(e.target.value)}
                    className="border border-slate-200 rounded-lg px-2 py-1.5 bg-white"
                    data-testid="cockpit-filter-status">
              {STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
            <select value={partner} onChange={(e) => setPartner(e.target.value)}
                    className="border border-slate-200 rounded-lg px-2 py-1.5 bg-white"
                    data-testid="cockpit-filter-partner">
              <option value="">Tutti i partner</option>
              {(config?.partners || []).map((p) => (
                <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Content: Stream / Kanban / Table */}
        {loading && items.length === 0 ? (
          <div className="text-center text-sm text-slate-500 py-10 flex items-center justify-center gap-2"
               data-testid="cockpit-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Caricamento richieste…
          </div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-200 rounded-2xl p-10 text-center"
               data-testid="cockpit-empty">
            <p className="text-sm text-slate-600 mb-4">
              {scope === "me"
                ? "Non hai richieste assegnate a te in questo periodo."
                : "Nessuna richiesta corrisponde ai filtri."}
            </p>
            {scope === "me" ? (
              <button
                onClick={() => setScope("all")}
                className="text-sm px-4 py-2 bg-[#163321] text-white rounded-full hover:bg-[#0F1F14]"
                data-testid="cockpit-empty-show-all"
              >
                Mostra tutte le richieste →
              </button>
            ) : (
              <button
                onClick={() => { setMacroSet(new Set()); setSubSet(new Set()); setStatus(""); setPartner(""); }}
                className="text-sm px-4 py-2 border border-slate-300 rounded-full hover:bg-slate-50"
                data-testid="cockpit-empty-clear-filters"
              >
                Azzera filtri
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3" data-testid="cockpit-stream">
            {items.map((it) => (
              <div key={it.id} data-highlight-id={it.id}>
                <StreamCard item={it}
                              expanded={expandedId === it.id}
                              onToggle={() => setExpandedId((cur) => cur === it.id ? null : it.id)}
                              onStatusChanged={() => load(page)}
                />
              </div>
            ))}
          </div>
        )}

        {/* Paginazione */}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between text-xs" data-testid="cockpit-pagination">
            <span className="text-slate-500">Pagina {page} di {totalPages}</span>
            <div className="flex items-center gap-1">
              <button onClick={() => load(page - 1)} disabled={page <= 1}
                      className="px-2 py-1 rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-50"
                      data-testid="cockpit-page-prev">‹ Prec</button>
              <button onClick={() => load(page + 1)} disabled={page >= totalPages}
                      className="px-2 py-1 rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-50"
                      data-testid="cockpit-page-next">Succ ›</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// FIX 2 (feb-2026) — Filtro Ramo GERARCHICO multi-select con accordion
// ═══════════════════════════════════════════════════════════════════════
function RamoHierarchicalFilter({
  config, macroSet, subSet,
  onMacroToggle, onSubToggle, onClear,
  expanded, onExpandToggle,
  open, onOpenChange,
  countsByMacro, countsBySub,
}) {
  const macros = config?.macro_rami || [];
  const totalSelected = macroSet.size + subSet.size;

  // Chiude dropdown al click esterno
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (!e.target.closest?.("[data-testid='cockpit-filter-ramo-root']")) {
        onOpenChange(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, onOpenChange]);

  // Etichetta compatta: "Tutti i rami" / "Auto" / "Auto +2"
  let label = "Tutti i rami";
  if (totalSelected > 0) {
    const parts = [];
    for (const m of macros) {
      if (macroSet.has(m.id)) parts.push(m.label);
    }
    for (const key of subSet) {
      const [mid, sid] = key.split(":");
      const macroCfg = macros.find(x => x.id === mid);
      const subCfg = macroCfg?.sub_rami?.find(x => x.id === sid);
      if (subCfg) parts.push(subCfg.label);
    }
    if (parts.length === 1) label = parts[0];
    else if (parts.length > 1) label = `${parts[0]} +${parts.length - 1}`;
  }

  return (
    <div className="relative" data-testid="cockpit-filter-ramo-root">
      <button
        type="button"
        onClick={() => onOpenChange(!open)}
        className={`inline-flex items-center gap-1.5 border rounded-lg px-2.5 py-1.5 bg-white transition-all
          ${totalSelected > 0
            ? "border-[#163321] text-[#163321] font-medium"
            : "border-slate-200 text-slate-700 hover:border-slate-300"}`}
        data-testid="cockpit-filter-ramo">
        <Filter className="w-3.5 h-3.5" />
        <span>{label}</span>
        {totalSelected > 0 && (
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#163321] text-white font-mono ml-1"
                data-testid="cockpit-filter-ramo-badge">{totalSelected}</span>
        )}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1 z-40 bg-white border border-slate-200 rounded-xl shadow-xl w-[320px] max-h-[420px] overflow-y-auto"
             data-testid="cockpit-filter-ramo-dropdown">
          <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 sticky top-0 bg-white">
            <span className="text-[11px] uppercase font-semibold text-slate-500">Filtra per ramo</span>
            {totalSelected > 0 && (
              <button
                type="button"
                onClick={onClear}
                className="text-[11px] text-slate-500 hover:text-red-600 inline-flex items-center gap-0.5"
                data-testid="cockpit-filter-ramo-clear">
                <X className="w-3 h-3" /> Azzera
              </button>
            )}
          </div>
          {macros.map((m) => {
            const isMacroChecked = macroSet.has(m.id);
            const macroCount = countsByMacro?.[m.id] || 0;
            const isExpanded = expanded.has(m.id);
            const hasSubs = (m.sub_rami || []).length > 0;
            return (
              <div key={m.id} className="border-b border-slate-50 last:border-b-0">
                <div className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50">
                  <button
                    type="button"
                    onClick={() => onMacroToggle(m.id)}
                    className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-all
                      ${isMacroChecked
                        ? "bg-[#163321] border-[#163321]"
                        : "bg-white border-slate-300 hover:border-[#163321]"}`}
                    data-testid={`cockpit-filter-ramo-macro-${m.id}`}
                    aria-checked={isMacroChecked}
                    role="checkbox">
                    {isMacroChecked && <Check className="w-3 h-3 text-white" />}
                  </button>
                  <button
                    type="button"
                    onClick={() => hasSubs && onExpandToggle(m.id)}
                    className="flex-1 flex items-center gap-1.5 text-left"
                    data-testid={`cockpit-filter-ramo-expand-${m.id}`}>
                    <span className="text-sm">{m.icon}</span>
                    <span className="text-xs font-medium text-slate-800">{m.label}</span>
                    <span className="text-[10px] text-slate-500 font-mono ml-auto">{macroCount}</span>
                    {hasSubs && (
                      <ChevronRight className={`w-3.5 h-3.5 text-slate-400 transition-transform
                        ${isExpanded ? "rotate-90" : ""}`} />
                    )}
                  </button>
                </div>
                {isExpanded && hasSubs && (
                  <div className="bg-slate-50/50 pl-8 pr-3 py-1"
                       data-testid={`cockpit-filter-ramo-sublist-${m.id}`}>
                    {(m.sub_rami || []).map((s) => {
                      const subKey = `${m.id}:${s.id}`;
                      const isSubChecked = subSet.has(subKey) || isMacroChecked;
                      const subCount = countsBySub?.[subKey] || 0;
                      return (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => onSubToggle(m.id, s.id)}
                          disabled={isMacroChecked}
                          className={`w-full flex items-center gap-2 py-1 text-left
                            ${isMacroChecked ? "opacity-60 cursor-not-allowed" : "hover:opacity-80"}`}
                          data-testid={`cockpit-filter-ramo-sub-${s.id}`}>
                          <div className={`w-3.5 h-3.5 rounded border-2 flex items-center justify-center shrink-0
                            ${isSubChecked
                              ? "bg-[#163321] border-[#163321]"
                              : "bg-white border-slate-300"}`}>
                            {isSubChecked && <Check className="w-2.5 h-2.5 text-white" />}
                          </div>
                          <span className="text-[11px] text-slate-700 flex-1">{s.label}</span>
                          <span className="text-[10px] text-slate-400 font-mono">{subCount}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════
// StreamCard — 1 card per richiesta
// ═══════════════════════════════════════════════════════════════════════
function StreamCard({ item, expanded, onToggle, onStatusChanged }) {
  const [statusOpen, setStatusOpen] = useState(false);
  const [selfLoading, setSelfLoading] = useState(false);
  // FIX iter84 — Optimistic status update: badge si aggiorna subito dopo PATCH
  // success senza attendere il rifetch dello stream (evita "flash del vecchio valore").
  const [localStatus, setLocalStatus] = useState(item.status);
  useEffect(() => { setLocalStatus(item.status); }, [item.status]);
  const tel = item.telefono ? item.telefono.replace(/\s|\+|-/g, "") : "";
  const email = item.email;

  const selfAssign = async (e) => {
    e.stopPropagation();
    setSelfLoading(true);
    try {
      await axios.post(`${API}/admin/richieste/${item.id}/assign`,
                        { self_assign: true },
                        { withCredentials: true });
      toast.success("Presa in carico");
      onStatusChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore assign");
    } finally { setSelfLoading(false); }
  };

  const changeStatus = async (e, newStatus) => {
    e.stopPropagation();
    if (!newStatus) return;
    // FIX feb-2026 — endpoint unificato Cockpit (prima ogni collection aveva URL diverso
    // e per do_quotations/cyber_questionari/fine_arts_pratiche/fondi_pensione_leads
    // NON esisteva alcun endpoint → UX rotta silenziosa).
    try {
      await axios.patch(
        `${API}/admin/cockpit/item/${item.id}/status`,
        { collection: item.collection, status: newStatus },
        { withCredentials: true },
      );
      setLocalStatus(newStatus);  // optimistic update badge
      toast.success(`Stato aggiornato: ${newStatus.replace(/_/g, " ")}`);
      setStatusOpen(false);
      onStatusChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore aggiornamento stato");
    }
  };

  const statusColor = {
    "nuovo": "bg-blue-100 text-blue-800",
    "in_lavorazione": "bg-amber-100 text-amber-800",
    "preventivato": "bg-purple-100 text-purple-800",
    "chiuso_positivo": "bg-emerald-100 text-emerald-800",
    "chiuso_negativo": "bg-red-100 text-red-800",
    "archiviato": "bg-slate-100 text-slate-600",
  }[localStatus] || "bg-slate-100 text-slate-700";

  return (
    <div className={`bg-white border rounded-2xl shadow-sm transition-all overflow-hidden
                     ${expanded ? "border-[#163321] ring-2 ring-emerald-100" : "border-slate-200 hover:border-slate-300"}`}
         data-testid={`cockpit-card-${item.id}`}>
      {/* Header card */}
      <button onClick={onToggle}
              className="w-full text-left p-4 flex items-start gap-3 hover:bg-slate-50/50 transition-all"
              data-testid={`cockpit-card-toggle-${item.id}`}>
        <div className="text-3xl mt-0.5">{item.macro_icon}</div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 mb-1">
            <span className="font-heading text-base font-semibold text-[#0F1F14]"
                  data-testid={`cockpit-card-name-${item.id}`}>
              {item.client_name}
            </span>
            <span className="text-slate-400 text-sm">·</span>
            <span className="text-xs text-slate-500" data-testid={`cockpit-card-sub-${item.id}`}>
              {item.macro_label} — {item.sub_label}
            </span>
            <span className="text-slate-300 text-xs">·</span>
            <span className="text-xs text-slate-400">{item.age}</span>
            {item.ricorrente && (
              <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200 font-semibold ml-1"
                    data-testid={`cockpit-card-recurring-${item.id}`}>
                <Flame className="w-3 h-3" /> {item.num_richieste}
              </span>
            )}
          </div>
          {/* Riepilogo automatico 2-3 righe */}
          <div className="space-y-0.5">
            {(item.summary_lines || []).map((l, i) => (
              <div key={i} className="text-[12px] text-slate-600">{l}</div>
            ))}
          </div>
        </div>
        {/* Stato + chevron */}
        <div className="flex flex-col items-end gap-1 shrink-0">
          <span className={`text-[10px] px-2 py-0.5 rounded font-semibold ${statusColor}`}
                data-testid={`cockpit-card-status-${item.id}`}>
            {localStatus}
          </span>
          {expanded ? <ChevronDown className="w-4 h-4 text-[#163321]" />
                    : <ChevronRight className="w-4 h-4 text-slate-400" />}
        </div>
      </button>

      {/* Azioni rapide */}
      <div className="px-4 pb-3 flex items-center flex-wrap gap-1.5"
           data-testid={`cockpit-card-actions-${item.id}`}>
        {tel && (
          <a href={`tel:${tel}`} onClick={(e) => e.stopPropagation()}
             className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-lg bg-emerald-50 text-emerald-800 hover:bg-emerald-100 border border-emerald-200"
             data-testid={`cockpit-card-tel-${item.id}`}>
            <Phone className="w-3 h-3" /> Chiama
          </a>
        )}
        {tel && (
          <a href={`https://wa.me/${tel}`} target="_blank" rel="noreferrer"
             onClick={(e) => e.stopPropagation()}
             className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-lg bg-green-50 text-green-800 hover:bg-green-100 border border-green-200"
             data-testid={`cockpit-card-wa-${item.id}`}>
            <MessageCircle className="w-3 h-3" /> WhatsApp
          </a>
        )}
        {email && (
          <a href={`mailto:${email}`} onClick={(e) => e.stopPropagation()}
             className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-lg bg-blue-50 text-blue-800 hover:bg-blue-100 border border-blue-200"
             data-testid={`cockpit-card-mail-${item.id}`}>
            <Mail className="w-3 h-3" /> Email
          </a>
        )}
        {!item.assigned_to && (
          <button onClick={selfAssign} disabled={selfLoading}
                  className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-lg bg-[#163321] text-white hover:bg-[#0F1F14] disabled:opacity-50"
                  data-testid={`cockpit-card-self-assign-${item.id}`}>
            {selfLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <ClipboardCheck className="w-3 h-3" />}
            Prendi in carico
          </button>
        )}
        {/* Cambio stato */}
        <div className="relative">
          <button onClick={(e) => { e.stopPropagation(); setStatusOpen(!statusOpen); }}
                  className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50"
                  data-testid={`cockpit-card-status-btn-${item.id}`}>
            Cambio stato
          </button>
          {statusOpen && (
            <div className="absolute top-full mt-1 z-20 bg-white border border-slate-200 rounded-lg shadow-lg py-1 min-w-[180px]"
                 onClick={(e) => e.stopPropagation()}
                 data-testid={`cockpit-card-status-menu-${item.id}`}>
              {STATUSES.filter((s) => s.key).map((s) => (
                <button key={s.key}
                        onClick={(e) => changeStatus(e, s.key)}
                        className="block w-full text-left text-[11px] px-3 py-1.5 hover:bg-emerald-50"
                        data-testid={`cockpit-card-status-set-${item.id}-${s.key}`}>
                  {s.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Accordion espanso (riuso iter71) */}
      {expanded && (
        <div className="border-t border-slate-200" data-testid={`cockpit-card-expanded-${item.id}`}>
          <RichiestaRiepilogoAccordion richiestaId={item.id} onClose={onToggle} />
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════
// FASE L2.2 — KanbanView (4 colonne per stato)
// ═══════════════════════════════════════════════════════════════════════
const KANBAN_COLUMNS = [
  { key: "nuovo", label: "Nuovo", color: "bg-blue-50 border-blue-200" },
  { key: "in_lavorazione", label: "In lavorazione", color: "bg-amber-50 border-amber-200" },
  { key: "preventivato", label: "Preventivato", color: "bg-violet-50 border-violet-200" },
  { key: "chiuso_positivo", label: "Chiuso positivo", color: "bg-emerald-50 border-emerald-200" },
];

function KanbanView({ items, onItemClick, expandedId, onStatusChanged }) {
  const grouped = KANBAN_COLUMNS.map(col => ({
    ...col,
    items: items.filter(it => (it.stato || it.status || "nuovo").toLowerCase() === col.key),
  }));
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="cockpit-kanban">
      {grouped.map(col => (
        <div key={col.key} className={`rounded-xl border ${col.color} p-3`} data-testid={`kanban-col-${col.key}`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-slate-700 uppercase">{col.label}</span>
            <span className="text-[10px] text-slate-500 bg-white/70 px-1.5 py-0.5 rounded">{col.items.length}</span>
          </div>
          <div className="space-y-2 max-h-[500px] overflow-y-auto">
            {col.items.map(it => (
              <div key={it.id}
                    onClick={() => onItemClick(it.id)}
                    className={`bg-white rounded-lg p-2.5 border cursor-pointer text-xs shadow-sm hover:shadow-md
                      ${expandedId === it.id ? "border-[#163321] ring-1 ring-[#163321]" : "border-slate-200"}`}
                    data-testid={`kanban-card-${it.id}`}>
                <div className="flex items-center gap-1.5 mb-1">
                  <span className="text-[9px] font-semibold px-1.5 py-0.5 bg-slate-100 text-slate-700 rounded uppercase">
                    {it.macro || it.ramo}
                  </span>
                  {it.is_recurring && <Flame className="w-3 h-3 text-orange-500" />}
                  <span className="text-[10px] text-slate-500 ml-auto">{it.age_label}</span>
                </div>
                <div className="font-medium text-slate-900 truncate">
                  {it.contact?.nome} {it.contact?.cognome}
                </div>
                {it.contact?.email && (
                  <div className="text-[10px] text-slate-500 truncate">{it.contact.email}</div>
                )}
              </div>
            ))}
            {col.items.length === 0 && (
              <div className="text-center text-[11px] text-slate-400 italic py-4">Nessuna</div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// FASE L2.2 — TableView (tabella compatta)
// ═══════════════════════════════════════════════════════════════════════
function TableView({ items, onItemClick, expandedId, onStatusChanged }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="cockpit-table">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase text-slate-600">
            <tr>
              <th className="text-left px-3 py-2 font-semibold">Quando</th>
              <th className="text-left px-3 py-2 font-semibold">Cliente</th>
              <th className="text-left px-3 py-2 font-semibold">Ramo</th>
              <th className="text-left px-3 py-2 font-semibold">Partner</th>
              <th className="text-left px-3 py-2 font-semibold">Stato</th>
              <th className="text-left px-3 py-2 font-semibold">Assegnato</th>
              <th className="text-right px-3 py-2 font-semibold">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {items.map(it => {
              const isExpanded = expandedId === it.id;
              return (
                <React.Fragment key={it.id}>
                  <tr onClick={() => onItemClick(it.id)}
                       className={`border-b border-slate-100 cursor-pointer hover:bg-slate-50 ${isExpanded ? "bg-emerald-50" : ""}`}
                       data-testid={`table-row-${it.id}`}>
                    <td className="px-3 py-2 text-slate-500 text-xs">{it.age_label}</td>
                    <td className="px-3 py-2 font-medium text-slate-900">
                      <div className="flex items-center gap-1.5">
                        {it.is_recurring && <Flame className="w-3.5 h-3.5 text-orange-500" />}
                        {it.contact?.nome} {it.contact?.cognome}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-xs uppercase text-slate-700">{it.macro || it.ramo}</td>
                    <td className="px-3 py-2 text-xs text-slate-600">{it.partner || "—"}</td>
                    <td className="px-3 py-2 text-xs">{it.stato || it.status || "nuovo"}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{it.assigned_to_name || (it.assigned_to ? "—" : "libero")}</td>
                    <td className="px-3 py-2 text-right">
                      <ChevronRight className={`w-4 h-4 inline-block transition-transform ${isExpanded ? "rotate-90" : ""}`} />
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr>
                      <td colSpan={7} className="p-0 bg-emerald-50/30">
                        <RichiestaRiepilogoAccordion richiestaId={it.id} onClose={() => onItemClick(it.id)} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
